import { openExternalLink } from "@gik/core/utils/browser";
import {
	createFacebookShareUrl,
	createTwitterShareUrl,
	getUserUpdateLink,
} from "@gik/core/utils/ShareUtils";
import { translationKeys } from "@gik/inkind-page/i18n/en";
import type InkindUserUpdate from "@gik/inkind-page/models/InkindUserUpdate";
import { Button } from "@gik/ui-legacy/Button";
import { HTMLParser } from "@gik/ui-legacy/HTMLParser";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon";
import { FacebookWhite } from "@gik/ui-legacy/SvgIcon/BrandIcons/Facebook";
import { TwitterWhite } from "@gik/ui-legacy/SvgIcon/BrandIcons/Twitter";
import { UI } from "@gik/ui-legacy/UIManager";
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

interface IShareOnSocialMediaProps {
	updateId: string;
	shareOnFacebook?: boolean;
	shareOnTwitter?: boolean;
	sharedToEmail?: boolean;
	buttonsRef?: React.MutableRefObject<HTMLDivElement>;
	closeFnRef?: React.MutableRefObject<() => boolean>;
}

export function ShareUpdateOnSocialMedia({
	updateId,
	shareOnFacebook,
	shareOnTwitter,
	sharedToEmail,
	buttonsRef,
	closeFnRef,
}: IShareOnSocialMediaProps) {
	const [sharedToFacebook, setSharedToFacebook] =
		React.useState<boolean>(false);
	const [sharedToTwitter, setSharedToTwitter] = React.useState<boolean>(false);
	const { t } = useTranslation();

	const updateLink = React.useMemo(
		() =>
			updateId &&
			encodeURIComponent(
				getUserUpdateLink({ id: updateId } as InkindUserUpdate),
			),
		[updateId],
	);

	function onClose(): boolean {
		if (shareOnFacebook && !sharedToFacebook) {
			handleShareToFacebookClicked(true);
			return !shareOnTwitter;
		}

		if (
			shareOnTwitter &&
			((!shareOnFacebook && !sharedToTwitter) ||
				(shareOnFacebook && sharedToFacebook && !sharedToTwitter))
		) {
			handleShareToTwitterClicked(true);
			return true;
		}

		return true;
	}

	closeFnRef.current = onClose;

	function handleShareToFacebookClicked(cancel?: boolean) {
		if (!cancel) {
			const shareUrl = createFacebookShareUrl(updateLink);
			openExternalLink(shareUrl);
		}

		setSharedToFacebook(true);

		if (!shareOnTwitter && !cancel) {
			UI.closeDialog();
		}
	}

	function handleShareToTwitterClicked(cancel?: boolean) {
		if (!cancel) {
			const shareUrl = createTwitterShareUrl(updateLink);
			openExternalLink(shareUrl);
		}

		setSharedToTwitter(true);

		if (!cancel) UI.closeDialog();
	}

	const buttons = (
		<>
			{shareOnFacebook && !sharedToFacebook && (
				<Button
					variant="facebook"
					onClick={() => handleShareToFacebookClicked()}
					prepend={<SvgIcon Icon={FacebookWhite} />}
				>
					Finish posting
				</Button>
			)}
			{shareOnTwitter &&
				((!shareOnFacebook && !sharedToTwitter) ||
					(shareOnFacebook && sharedToFacebook && !sharedToTwitter)) && (
					<Button
						variant="twitter"
						onClick={() => handleShareToTwitterClicked()}
						prepend={<SvgIcon Icon={TwitterWhite} />}
					>
						Finish Posting
					</Button>
				)}
		</>
	);

	return (
		<div>
			<p>
				{t(
					sharedToEmail
						? translationKeys.UpdateSentToPageEmailAndFollowersLabel
						: translationKeys.UpdateSentToPageLabel,
				)}
			</p>
			<p>
				<HTMLParser rawHtml={t(translationKeys.OneMoreStep)} />{" "}
				{shareOnFacebook && !sharedToFacebook && "Facebook"}
				{shareOnTwitter &&
					((!shareOnFacebook && !sharedToTwitter) ||
						(shareOnFacebook && sharedToFacebook && !sharedToTwitter)) &&
					"Twitter"}
			</p>

			{buttonsRef?.current ? (
				ReactDOM.createPortal(buttons, buttonsRef.current)
			) : (
				<div className={"tw-flex tw-flex-row tw-justify-center"}>{buttons}</div>
			)}
		</div>
	);
}
